/**
 * DASHBOARD-PARTICIPATION-PROGRESS-ANIMATED.WIDGET
 * Courbe de participation cumulée + barres de participation quotidienne —
 * remplace le rendu Plotly (dashboard-participation-progress.widget.tsx, laissé
 * intact en référence/fallback) par du SVG custom, responsive (viewBox, comme
 * dashboard-results-axes-animated.widget.tsx) pour ne jamais déborder de sa card,
 * avec une entrée animée : barres qui poussent depuis le bas, ligne qui se
 * dessine (pathLength), points qui popent une fois la ligne arrivée.
 */

import { withTranslation, WithTranslation } from "react-i18next"
import { useEffect, useState } from "react"
import { countBy, orderBy } from "lodash"
import { motion, AnimatePresence } from "framer-motion"
import { staggerDelay } from "@/utils/stagger-delay.utils"
import { DateType, SurveyInvitation } from "./dashboard-participation.widget"
import PageLoader from "@/components/page-loader"
import { STATUS_LOADING } from "@/redux/_status.types"
import { Session } from "@/redux/_session.types"
import { connect } from "react-redux"

interface StateProps {
  _session: Session
}

interface OwnProps {
  invitations: SurveyInvitation[]
  selectedDateType: DateType
}

type Props = StateProps & OwnProps & WithTranslation

const WIDTH = 760
const HEIGHT = 350
const MARGIN = { left: 42, right: 30, top: 20, bottom: 85 }
const CHART_WIDTH = WIDTH - MARGIN.left - MARGIN.right
const CHART_HEIGHT = HEIGHT - MARGIN.top - MARGIN.bottom
const BAR_COLOR = "#9CA3AF"
const LEGEND_WIDTH = 150

function DashboardParticipationProgressAnimatedWidget(props: Props) {
  const { t } = props

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [dates, setDates] = useState<string[]>([])
  const [dailyValues, setDailyValues] = useState<number[]>([])
  const [cumulativeValues, setCumulativeValues] = useState<number[]>([])
  const [hovered, setHovered] = useState<{ x: number; y: number; date: string; value: number; color: string } | null>(
    null
  )

  useEffect(() => {
    function sumItems(array: number[]): number[] {
      let sum: number = 0
      return array.map((x: number) => (sum = (sum || 0) + x))
    }

    function formateData() {
      setIsLoading(true)

      const selectedDateType: DateType = props.selectedDateType
      const filteredInvitations: SurveyInvitation[] = props.invitations.filter((x: SurveyInvitation) => x[selectedDateType])

      const items: object = countBy(
        orderBy(filteredInvitations, selectedDateType).map((x: SurveyInvitation) =>
          new Intl.DateTimeFormat(undefined, {}).format(new Date(x[selectedDateType]!))
        )
      )

      const rawValues = Object.values(items) as number[]

      setDates(Object.keys(items))
      setDailyValues(rawValues.map((x: number) => (x / props.invitations.length) * 100))
      setCumulativeValues(sumItems(rawValues).map((x: number) => (x / props.invitations.length) * 100))

      setIsLoading(false)
    }

    if (props.invitations.length > 0) {
      formateData()
    } else {
      setIsLoading(false)
    }
  }, [t, props.invitations, props.selectedDateType])

  if (isLoading) {
    return <PageLoader status={STATUS_LOADING} />
  }

  if (props.invitations.length === 0 || dates.length === 0) {
    return <div>{t("disparity_level_0")}</div>
  }

  const n = dates.length
  const dataMax = Math.max(1, ...dailyValues, ...cumulativeValues)
  const niceMax = Math.max(20, Math.ceil(dataMax / 20) * 20)
  const ticks = [0, niceMax * 0.25, niceMax * 0.5, niceMax * 0.75, niceMax]

  const slotWidth = CHART_WIDTH / n
  const barWidth = slotWidth * 0.75

  function xCenter(i: number): number {
    return MARGIN.left + slotWidth * (i + 0.5)
  }

  function yFor(value: number): number {
    return MARGIN.top + CHART_HEIGHT - (value / niceMax) * CHART_HEIGHT
  }

  const linePoints = cumulativeValues.map((v, i) => `${xCenter(i)},${yFor(v)}`).join(" ")

  return (
    <div className="rel">
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        style={{ width: "100%", height: "auto", maxWidth: WIDTH, display: "block" }}
        aria-label={t("participation_cumulated")}>
      {ticks.map((tick, i) => (
        <g key={i}>
          <line
            x1={MARGIN.left}
            y1={yFor(tick)}
            x2={MARGIN.left + CHART_WIDTH}
            y2={yFor(tick)}
            stroke="#f0f0f0"
            strokeWidth={1}
          />
          <text x={MARGIN.left - 10} y={yFor(tick)} textAnchor="end" dominantBaseline="middle" fontSize={12} fill="#111C2B">
            {Math.round(tick)}
          </text>
        </g>
      ))}

      <line
        x1={MARGIN.left}
        y1={MARGIN.top + CHART_HEIGHT}
        x2={MARGIN.left + CHART_WIDTH}
        y2={MARGIN.top + CHART_HEIGHT}
        stroke="#111C2B"
        strokeWidth={1}
      />

      {dates.map((date, i) => {
        const labelX = xCenter(i)
        const labelY = MARGIN.top + CHART_HEIGHT + 20
        return (
          <text
            key={date}
            x={labelX}
            y={labelY}
            textAnchor="end"
            fontSize={11}
            fill="#111C2B"
            transform={`rotate(-35 ${labelX} ${labelY})`}>
            {date}
          </text>
        )
      })}

      {dailyValues.map((value, i) => {
        const y = yFor(value)
        const height = MARGIN.top + CHART_HEIGHT - y
        return (
          <motion.rect
            key={i}
            x={xCenter(i) - barWidth / 2}
            y={y}
            width={barWidth}
            height={height}
            rx={2}
            fill={BAR_COLOR}
            fillOpacity={0.75}
            stroke="#6b7280"
            strokeWidth={1}
            onMouseEnter={() => setHovered({ x: xCenter(i), y, date: dates[i], value: Math.round(value), color: "#6b7280" })}
            onMouseLeave={() => setHovered((h) => (h?.date === dates[i] && h.color === "#6b7280" ? null : h))}
            style={{ transformBox: "fill-box", transformOrigin: "50% 100%", cursor: "pointer" }}
            initial={{ scaleY: 0 }}
            animate={{ scaleY: 1 }}
            transition={{ delay: staggerDelay(i, 0.03), duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
          />
        )
      })}

      <motion.polyline
        points={linePoints}
        fill="none"
        stroke={props._session.accountColors.brandPrimary}
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 1, ease: "easeInOut" }}
      />

      {cumulativeValues.map((value, i) => (
        <motion.circle
          key={i}
          cx={xCenter(i)}
          cy={yFor(value)}
          r={4.5}
          fill={props._session.accountColors.brandPrimary}
          onMouseEnter={() =>
            setHovered({
              x: xCenter(i),
              y: yFor(value),
              date: dates[i],
              value: Math.round(value),
              color: props._session.accountColors.brandPrimary
            })
          }
          onMouseLeave={() =>
            setHovered((h) => (h?.date === dates[i] && h.color === props._session.accountColors.brandPrimary ? null : h))
          }
          style={{ cursor: "pointer" }}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: staggerDelay(i, 0.05, 0.3), duration: 0.3, ease: [0.34, 1.56, 0.64, 1] }}
        />
      ))}

      <g
        transform={`translate(${MARGIN.left + CHART_WIDTH - LEGEND_WIDTH}, ${yFor(niceMax * 0.5) - 20})`}>
        <line x1={0} y1={0} x2={20} y2={0} stroke={props._session.accountColors.brandPrimary} strokeWidth={2.5} />
        <circle cx={10} cy={0} r={4} fill={props._session.accountColors.brandPrimary} />
        <text x={28} y={4} fontSize={12} fill="#111C2B">
          % {t("participation_cumulated")}
        </text>

        <rect x={0} y={22} width={20} height={14} fill={BAR_COLOR} fillOpacity={0.75} stroke="#6b7280" strokeWidth={1} rx={2} />
        <text x={28} y={33} fontSize={12} fill="#111C2B">
          % {t("participation_daily")}
        </text>
      </g>
      </svg>

      <AnimatePresence>
        {hovered && (
          <motion.div
            initial={{ opacity: 0, y: 4, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="abs pointer-events-none bg-white rounded-lg shadow-md border border-gray-100 px-3 py-2"
            style={{
              left: `${(hovered.x / WIDTH) * 100}%`,
              top: `${(hovered.y / HEIGHT) * 100}%`,
              transform: "translate(-50%, calc(-100% - 10px))",
              whiteSpace: "nowrap",
              zIndex: 20
            }}>
            <div className="text-sm font-semibold" style={{ color: hovered.color }}>
              {hovered.value}%
            </div>
            <div className="text-sm grey-t">{hovered.date}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session
})

export default connect(mapStateToProps)(withTranslation()(DashboardParticipationProgressAnimatedWidget))
